import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { buildImageUrl } from "../../../config/config";
import useToggleSidebar from "../../../components/togglesidebar";
import Sidebar from '../../sidebar';
import Header from '../../Header';
import Footer from '../../footer';
import Modal from '../../../components/Modal/Modal';

function ClaimRequests() {
    const { isSidebarOpen, toggleSidebar } = useToggleSidebar();
    const navigate = useNavigate();
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState("pending"); // Default to pending
    const [actionTarget, setActionTarget] = useState(null); // { action: 'approve'/'reject', claim: {...} }
    const [viewClaim, setViewClaim] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch KYC submissions based on selected status filter
            const response = await api.get('/kyc/admin/all', {
                params: {
                    status: statusFilter === 'all' ? '' : statusFilter
                }
            });

            if (response.data.success) {
                setClaims(response.data.kycs);
            } else {
                setError(response.data.message || "Failed to fetch KYC requests.");
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                localStorage.removeItem("adminToken");
                localStorage.removeItem("adminUser");
                navigate('/admin/login', { replace: true });
                return;
            }
            setError(err.response?.data?.message || err.message || "An error occurred.");
        } finally {
            setLoading(false);
        }
    }, [statusFilter, navigate]);

    useEffect(() => {
        const storedToken = localStorage.getItem("adminToken");
        if (!storedToken) {
            navigate("/admin/login", { replace: true });
            return;
        }
        fetchData();
    }, [fetchData, navigate]);

    const handleAction = async () => {
        if (!actionTarget) return;

        const { action, claim } = actionTarget;
        setActionTarget(null);

        try {
            if (action === 'approve') {
                const response = await api.put(`/kyc/admin/${claim._id}/review`, {
                    status: 'approved',
                    adminNotes: 'Approved by admin'
                });
                setSuccessMessage(response.data.message || "KYC approved successfully!");
            } else if (action === 'reject') {
                const response = await api.put(`/kyc/admin/${claim._id}/review`, {
                    status: 'rejected',
                    adminNotes: 'Rejected by admin'
                });
                setSuccessMessage(response.data.message || "KYC rejected successfully!");
            }
            // Refetch data to get latest list
            fetchData();

            // Hide success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage("");
            }, 3000);

        } catch (err) {
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                localStorage.removeItem("adminToken");
                localStorage.removeItem("adminUser");
                navigate('/admin/login', { replace: true });
                return;
            }
            const errorMsg = err.response?.data?.message || err.message || `Failed to ${action} KYC.`;
            setError(errorMsg);
            setTimeout(() => setError(null), 3000);
        }
    };

    const closeView = () => {
        setViewClaim(null);
    };

    return (
        <>
            <Helmet><title>Admin | Claim Requests</title></Helmet>
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`} onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}>
                <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
                    <Header toggleSidebar={toggleSidebar} />
                    <section className='content'>
                        <div className="breadcrumb-main-bx">
                            <div className="breadcrumb-bx">
                                <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                                <div className="breadcrumb-link breadcrumb-active">Claim Requests</div>
                            </div>
                        </div>
                        <div className='container-fluid'>
                            <div className='row'>
                                <div className='col-md-12'>
                                    <div className='card'>
                                        <div className="card-header">
                                            <div>KYC Verification Requests</div>
                                            <div className="form-group mt-2">
                                                <label htmlFor="statusFilter">Filter by Status:</label>
                                                <select
                                                    id="statusFilter"
                                                    className="form-control"
                                                    value={statusFilter}
                                                    onChange={(e) => setStatusFilter(e.target.value)}
                                                    style={{ maxWidth: '200px' }}
                                                >
                                                    <option value="all">All</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="approved">Approved</option>
                                                    <option value="needs_revision">Needs Revision</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className='card-body'>
                                            {successMessage && (
                                                <div className="alert alert-success" role="alert">
                                                    {successMessage}
                                                </div>
                                            )}
                                            <div className='table-responsive'>
                                                <table className="table table-bordered">
                                                    <thead>
                                                        <tr>
                                                            <th>No.</th>
                                                            <th>Company Name</th>
                                                            <th>Contact Person</th>
                                                            <th>Contact Email</th>
                                                            <th>Status</th>
                                                            <th>Submitted</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {loading ? (
                                                            <tr><td colSpan="7" className="text-center">Loading...</td></tr>
                                                        ) : error ? (
                                                            <tr><td colSpan="7" className="text-center text-danger">{error}</td></tr>
                                                        ) : claims.length > 0 ? (
                                                            claims.map((claim, index) => (
                                                                <tr key={claim._id}>
                                                                    <td>{index + 1}</td>
                                                                    <td>{claim.companyName || 'N/A'}</td>
                                                                    <td>{claim.contactPerson || 'N/A'}</td>
                                                                    <td>
                                                                        {claim.contactEmail ? (
                                                                            <a href={`mailto:${claim.contactEmail}?subject=${encodeURIComponent(`KYC Inquiry: ${claim.companyName || ''}`)}`} style={{ color: '#0d6efd', textDecoration: 'underline' }}>{claim.contactEmail}</a>
                                                                        ) : (
                                                                            '-'
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <span className={`badge ${claim.status === 'approved' ? 'bg-success' :
                                                                            claim.status === 'pending' ? 'bg-warning text-dark' :
                                                                                'bg-secondary'
                                                                            }`}>
                                                                            {claim.status}
                                                                        </span>
                                                                    </td>
                                                                    <td>{new Date(claim.submittedAt).toLocaleString()}</td>
                                                                    <td>
                                                                        <button className="btn btn-primary btn-sm" onClick={() => setViewClaim(claim)}>
                                                                            View
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr><td colSpan="7" className="text-center">No claim requests found for the selected status.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>

            {viewClaim && (
                <div
                    className="modal fade show"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={closeView}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">KYC Details</h5>
                                <button type="button" className="btn-close" onClick={closeView} aria-label="Close" />
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div><strong>Provider:</strong> {viewClaim?.providerId?.name || '-'}</div>
                                        <div className="mt-2"><strong>Provider URL:</strong> {viewClaim?.providerId?.url ? <a href={viewClaim.providerId.url} target="_blank" rel="noreferrer">{viewClaim.providerId.url}</a> : '-'}</div>
                                        <div className="mt-2"><strong>Submitted By:</strong> {viewClaim?.userId ? `${viewClaim.userId.firstName || ''} ${viewClaim.userId.lastName || ''}`.trim() || viewClaim.userId.email || '-' : '-'}</div>
                                        <div><strong>Company Name:</strong> {viewClaim.companyName || '-'}</div>
                                        <div className="mt-2"><strong>Registered Address:</strong> {viewClaim.registeredAddress || '-'}</div>
                                        <div className="mt-2"><strong>Website:</strong> {viewClaim.website ? <a href={viewClaim.website} target="_blank" rel="noreferrer">{viewClaim.website}</a> : '-'}</div>
                                        <div className="mt-2"><strong>Contact Person:</strong> {viewClaim.contactPerson || '-'}</div>
                                        <div className="mt-2"><strong>Contact Email:</strong> {viewClaim.contactEmail ? <a href={`mailto:${viewClaim.contactEmail}?subject=${encodeURIComponent(`KYC Inquiry: ${viewClaim.companyName || ''}`)}`} style={{ color: '#0d6efd', textDecoration: 'underline' }}>{viewClaim.contactEmail}</a> : '-'}</div>
                                        <div className="mt-2"><strong>Contact Phone:</strong> {viewClaim.contactPhone || '-'}</div>
                                        <div className="mt-2"><strong>Business Type:</strong> {viewClaim.businessType || '-'}</div>
                                        <div className="mt-2"><strong>Business License:</strong> {viewClaim.businessLicense || '-'}</div>
                                        <div className="mt-2"><strong>Tax ID:</strong> {viewClaim.taxId || '-'}</div>
                                        <div className="mt-2"><strong>Year Established:</strong> {viewClaim.yearEstablished ?? '-'}</div>
                                        <div className="mt-2"><strong>Description:</strong> {viewClaim.description || '-'}</div>
                                        <div className="mt-2"><strong>GEO Latitude:</strong> {viewClaim.geoLatitude ?? '-'}</div>
                                        <div className="mt-2"><strong>GEO Longitude:</strong> {viewClaim.geoLongitude ?? '-'}</div>
                                        <div className="mt-2"><strong>Video Call Scheduled:</strong> {viewClaim.videoCallIsScheduled ? 'Yes' : 'No'}</div>
                                        <div className="mt-2"><strong>Video Call Date:</strong> {viewClaim.videoCallScheduledDate || '-'}</div>
                                        <div className="mt-2"><strong>Video Call Time:</strong> {viewClaim.videoCallScheduledTime || '-'}</div>
                                        <div className="mt-2"><strong>Status:</strong> {viewClaim.status}</div>
                                        <div className="mt-2"><strong>Admin Notes:</strong> {viewClaim.adminNotes || '-'}</div>
                                        <div className="mt-2"><strong>Submitted:</strong> {new Date(viewClaim.submittedAt).toLocaleString()}</div>
                                    </div>
                                    <div className="col-md-6">
                                        <div><strong>Proof Document:</strong></div>
                                        {viewClaim.proofDocument ? (() => {
                                            const proofUrl = buildImageUrl(viewClaim.proofDocument);
                                            const ext = String(proofUrl || '')
                                                .split('?')[0]
                                                .split('#')[0]
                                                .split('.')
                                                .pop()
                                                ?.toLowerCase();
                                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');

                                            return (
                                                <div className="mt-2">
                                                    {isImage ? (
                                                        <img
                                                            src={proofUrl}
                                                            alt="KYC Proof"
                                                            style={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 6, border: '1px solid #ddd' }}
                                                        />
                                                    ) : (
                                                        <a href={proofUrl} target="_blank" rel="noreferrer">
                                                            View / Download Proof
                                                        </a>
                                                    )}
                                                </div>
                                            );
                                        })() : (
                                            <div className="mt-2">-</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeView}>Close</button>
                                {viewClaim.status === 'pending' && (
                                    <>
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={() => {
                                                closeView();
                                                setActionTarget({ action: 'approve', claim: viewClaim });
                                            }}
                                            disabled={false}
                                        >
                                            Approve
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={() => {
                                                closeView();
                                                setActionTarget({ action: 'reject', claim: viewClaim });
                                            }}
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {actionTarget && (
                <Modal
                    isOpen={!!actionTarget}
                    onClose={() => setActionTarget(null)}
                    onConfirm={handleAction}
                    title={`${actionTarget.action === 'approve' ? 'Approve' : 'Reject'} Claim`}
                    message={`Are you sure you want to ${actionTarget.action} this KYC request for "${actionTarget.claim.companyName}" by ${actionTarget.claim.contactEmail || '-'}?`}
                    confirmText={actionTarget.action === 'approve' ? 'Approve' : 'Reject'}
                />
            )}
        </>
    );
}

export default ClaimRequests;
